/**
 * Finalizarea comenzii.
 *
 * Pagina asta e locul unde se concentreaza aproape toate deciziile din
 * ARHITECTURA.md, si fiecare are un efect vizibil:
 *
 *  - ADR-003: identitatea completa a vanzatorului (denumire, CUI, sediu,
 *    telefon) e afisata DEASUPRA butonului de confirmare, iar butonul trimite
 *    exact `vendorId`-ul afisat. Daca intre timp core-ul a ales alta firma,
 *    raspunsul e `SELLER_MISMATCH` si comanda nu pleaca — nu se rescrie tacit.
 *  - ADR-004: taxa de livrare e linie separata, cu cota ei; bacsis nu exista.
 *  - ADR-005: singura metoda de plata e numerarul la livrare.
 *  - ADR-009: estimarea se cere o singura data aici, la intrarea pe pagina.
 *  - ADR-018: campurile de facturare pe firma sunt optionale si se propaga in
 *    comanda de canal.
 *  - ADR-023 / acoperire: daca vitrina nu poate acoperi tot cosul, confirmarea
 *    se blocheaza si se listeaza ce lipseste.
 *
 * Ordinea sectiunilor e cea a intrebarilor clientului: unde si cand ajunge,
 * cine il suna, cum plateste, ce a comandat, cat plateste, cine ii vinde.
 * Datele de contact sunt REtinute pe dispozitiv dupa prima comanda (fara cont),
 * validarea e in linie, iar butonul de confirmare sta intr-o bara lipita jos,
 * cu totalul si vanzatorul in ea — ca la contractare firma sa fie in vedere.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  CONSENT_TEXTS_RO,
  CONSENT_TEXT_VERSION_DEFAULT,
  CORE_ERROR_CODES,
  consentAllows,
  formatRon,
  resolveSellerMarketingTemplate,
  sellerMarketingText,
  type CheckoutRequest,
  type PaymentMethod,
  groupMarketingText,
} from "@deliveryapp/shared";

import { ApiError, requestQuote, submitCheckout } from "../api/client";
import { useAddressSheet } from "../components/AddressSheetProvider";
import { formatAddress, formatAddressDetails } from "../components/AddressForm";
import { useBootstrap } from "../components/BootstrapProvider";
import { rememberOrder, rememberedLinesFromCart, useCart } from "../components/CartProvider";
import {
  clearCustomerProfile,
  isAcceptablePhone,
  isPlausibleEmail,
  readCustomerProfile,
  writeCustomerProfile,
} from "../components/customer-profile";
import { formatClock } from "../components/EtaWindow";
import { IconCash, IconClock, IconEdit, IconPin, IconWarning } from "../components/icons";
import { nutritionBasisLabel, nutritionRows } from "../components/offer-merchandising";
import { isUsableSellerPhone, SellerIdentityCard } from "../components/SellerIdentityCard";
import { quotedPriceForLine } from "../components/quote-pricing";
import { EmptyState, ErrorState, LoadingState } from "../components/StateScreen";
import { TotalsPanel } from "../components/TotalsPanel";
import { useLoadable } from "../components/useLoadable";
import { Alert, Button, ChoiceRow, Field, Price, SectionHeading, TextArea } from "../components/ui";
import { useConsent } from "../consent/ConsentProvider";
import { newEventId, readMetaCookies, trackEvent } from "../consent/tracking";

/**
 * Metodele de plata pe care aceasta versiune a aplicatiei le poate NUMI. O
 * metoda pe care serverul o accepta si noi nu o putem numi se trateaza ca
 * diferenta de versiune (ADR-002).
 */
const PAYMENT_LABELS: Record<string, { label: string; hint: string }> = {
  cash_on_delivery: { label: "Numerar la livrare", hint: "Plătești curierului, care îți predă bonul fiscal emis de restaurant." },
};

interface CustomerDraft {
  name: string;
  phone: string;
  email: string;
  notes: string;
  changeFor: string;
  billingRequested: boolean;
  billingTaxId: string;
  billingLegalName: string;
  billingAddress: string;
  termsAccepted: boolean;
  marketingOptIn: boolean;
  rememberMe: boolean;
}

function initialDraft(marketKey: string): CustomerDraft {
  const profile = readCustomerProfile(marketKey);
  return {
    name: profile?.name ?? "",
    phone: profile?.phone ?? "",
    email: profile?.email ?? "",
    notes: "",
    changeFor: "",
    billingRequested: false,
    billingTaxId: "",
    billingLegalName: "",
    billingAddress: "",
    termsAccepted: false,
    marketingOptIn: false,
    rememberMe: true,
  };
}

type Touched = Partial<Record<"name" | "phone" | "email" | "billingTaxId", boolean>>;

function Section({ id, title, action, children }: { id: string; title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3" aria-labelledby={id}>
      <div className="flex items-center justify-between gap-3">
        <h2 id={id} className="text-base font-bold text-ink">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Checkout() {
  const { marketKey, bootstrap, config, locale, t } = useBootstrap();
  const { state: consentState } = useConsent();
  const { session, acceptSeller, clearLines, setVoucherCode, itemCount, hasUnavailableLines } = useCart();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState<CustomerDraft>(() => initialDraft(marketKey));
  const [touched, setTouched] = useState<Touched>({});
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const addressSheet = useAddressSheet();
  const [notesOpen, setNotesOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  const address = session.address;
  const storefrontSlug = session.storefrontSlug;
  const lines = useMemo(() => session.lines.filter((line) => line.unavailableAtAddress !== true), [session.lines]);
  // Cheia poarta si `lineId`, deci si optiunile: aceeasi oferta cu alt blat e
  // alt cos, si trebuie reestimata (ADR-024 — `priceDelta` schimba totalul).
  const linesKey = useMemo(() => lines.map((line) => `${line.lineId}x${line.qty}`).join("|"), [lines]);

  const quote = useLoadable(
    (signal) => {
      if (address === null || storefrontSlug === null || lines.length === 0) {
        return Promise.reject(
          new ApiError({ status: 0, code: CORE_ERROR_CODES.VALIDATION_FAILED, message: "Comanda nu are adresă, vitrină sau produse." }),
        );
      }
      return requestQuote(
        marketKey,
        {
          storefrontSlug,
          items: lines.map((line) => ({
            offerId: line.offerId,
            qty: line.qty,
            notes: line.notes,
            selectedOptions: line.selectedOptions.length > 0 ? line.selectedOptions : undefined,
          })),
          dropoff: { lat: address.lat, lng: address.lng, city: address.city },
          reason: "checkout",
          voucherCode: session.voucherCode,
        },
        signal,
      );
    },
    [marketKey, storefrontSlug, address?.lat, address?.lng, linesKey, session.voucherCode],
  );

  const seller = quote.data?.seller ?? null;
  const sellerChanged = seller !== null && session.acceptedVendorId !== null && session.acceptedVendorId !== seller.vendorId;

  const acceptedByBoth = useMemo(() => {
    const fromQuote = quote.data?.paymentMethods ?? [];
    const fromConfig = config.payments.methods;
    return fromQuote.filter((method) => fromConfig.includes(method));
  }, [quote.data?.paymentMethods, config.payments.methods]);
  const allowedMethods = useMemo(() => acceptedByBoth.filter((method) => PAYMENT_LABELS[method] !== undefined), [acceptedByBoth]);
  const unnamedMethodCount = acceptedByBoth.length - allowedMethods.length;

  useEffect(() => {
    if (seller === null || session.acceptedVendorId !== null) return;
    acceptSeller(seller.vendorId);
  }, [seller, session.acceptedVendorId, acceptSeller]);

  useEffect(() => {
    if (paymentMethod !== null && allowedMethods.includes(paymentMethod)) return;
    setPaymentMethod(allowedMethods[0] ?? null);
  }, [allowedMethods, paymentMethod]);

  if (itemCount === 0 || lines.length === 0) {
    return (
      <EmptyState
        title="Coșul este gol"
        description="Adaugă produse înainte de finalizare."
        action={<Button onClick={() => navigate("/")}>Înapoi la restaurante</Button>}
      />
    );
  }

  if (hasUnavailableLines) {
    return (
      <EmptyState
        title="Coșul are produse indisponibile la adresa ta"
        description="Scoate-le din coș sau schimbă adresa, apoi revino la finalizare."
        action={<Button onClick={() => navigate("/cos")}>Înapoi la coș</Button>}
      />
    );
  }

  if (address === null || storefrontSlug === null) {
    return (
      <>
        <EmptyState
          title="Mai lipsește adresa"
          description="Comanda nu poate fi estimată fără adresa de livrare."
          action={<Button onClick={() => addressSheet.open()}>Adaugă adresa</Button>}
        />
      </>
    );
  }

  if (quote.error !== null) {
    const voucherGone = quote.error.code === CORE_ERROR_CODES.VOUCHER_INVALID;
    const recoverableOrderRule =
      quote.error.code === CORE_ERROR_CODES.MIN_ORDER_NOT_REACHED ||
      quote.error.code === CORE_ERROR_CODES.ADDRESS_OUT_OF_COVERAGE ||
      quote.error.code === CORE_ERROR_CODES.NO_VENDOR_AVAILABLE ||
      voucherGone;
    if (recoverableOrderRule) {
      const needsProducts = quote.error.code === CORE_ERROR_CODES.MIN_ORDER_NOT_REACHED;
      return (
        <div className="mx-auto max-w-readable py-6">
          <Alert
            tone="warning"
            title={voucherGone ? "Voucherul nu mai poate fi folosit" : needsProducts ? "Mai trebuie produse în coș" : "Adresa nu mai poate fi livrată acum"}
            actions={
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => {
                    // Un voucher consumat / anulat între coș și confirmare nu se
                    // „reîncearcă": se scoate din sesiune și comanda se reia fără el.
                    if (voucherGone) {
                      setVoucherCode(null);
                      quote.reload();
                      return;
                    }
                    if (needsProducts) navigate(`/vitrina/${encodeURIComponent(storefrontSlug)}`);
                    else addressSheet.open();
                  }}
                >
                  {voucherGone ? "Continuă fără voucher" : needsProducts ? "Adaugă produse" : "Schimbă adresa"}
                </Button>
                <Button variant="secondary" onClick={() => navigate("/cos")}>
                  Înapoi la coș
                </Button>
              </div>
            }
          >
            <p>{quote.error.message}</p>
            <p className="mt-1 text-ink-muted">
              {needsProducts
                ? "Coșul și adresa rămân salvate. După ce completezi comanda, taxa și totalul se verifică din nou."
                : "Nu trimitem o comandă pe care restaurantul nu o poate livra. Alege altă adresă și recalculăm înainte de confirmare."}
            </p>
          </Alert>
        </div>
      );
    }
    return <ErrorState title="Comanda nu a putut fi estimată" error={quote.error} onRetry={quote.reload} />;
  }
  if (quote.data === null) return <LoadingState label="Se calculează comanda" />;

  const currentQuote = quote.data;
  const totals = currentQuote.totals;
  const foodFactsReady =
    typeof currentQuote.foodFactsFingerprint === "string" &&
    Array.isArray(currentQuote.foodFacts) &&
    currentQuote.foodFacts.every(
      (facts) =>
        Array.isArray(facts.selectedOptionNutrition) &&
        facts.selectedOptionIds.every((optionId) => facts.selectedOptionNutrition!.some((entry) => entry.optionId === optionId)),
    );
  const coverageIncomplete = currentQuote.coverage.status !== "full";
  const missingLines = lines.filter((line) => currentQuote.coverage.missingOfferIds.includes(line.offerId));
  const allAllergens = [...new Set(currentQuote.foodFacts.flatMap((facts) => facts.allergens))];

  const showNameError = showValidation || touched.name === true;
  const showPhoneError = showValidation || touched.phone === true;
  const showEmailError = showValidation || touched.email === true;
  const nameError = showNameError && customer.name.trim().length < 3 ? "Completează numele." : null;
  const phoneError = showPhoneError && !isAcceptablePhone(customer.phone) ? "Scrie un număr de telefon valid, la care poate suna curierul." : null;
  const emailError = showEmailError && !isPlausibleEmail(customer.email) ? "Adresa de e-mail nu pare corectă." : null;
  const billingError =
    (showValidation || touched.billingTaxId === true) && customer.billingRequested && customer.billingTaxId.trim().length < 2 ? "Completează CUI-ul firmei." : null;
  const termsError = showValidation && !customer.termsAccepted ? "Acceptarea termenilor este necesară." : null;

  const reloadRequired = submitError !== null && submitError.requiresReload;

  const blockers: string[] = [];
  if (seller === null) blockers.push("Vânzătorul nu este stabilit pentru această comandă.");
  if (sellerChanged) blockers.push("Vânzătorul afișat s-a schimbat și trebuie confirmat din nou.");
  if (coverageIncomplete) blockers.push("Restaurantul nu poate pregăti toate produsele din coș.");
  if (paymentMethod === null) blockers.push("Nu există nicio metodă de plată pe care o putem afișa.");
  if (reloadRequired) blockers.push("Aplicația trebuie reîncărcată înainte de a trimite comanda.");
  if (!foodFactsReady) blockers.push("Declarația actuală de alergeni nu a putut fi confirmată.");

  const destinatari = bootstrap.recipients ?? [];
  const esteGrup = destinatari.length > 1;
  const bifaMarketingDisponibila = bootstrap.sellerMarketingAvailable !== false;
  const consentTextVersion = bootstrap.consentVersion ?? config.consent?.textVersion ?? CONSENT_TEXT_VERSION_DEFAULT;
  const sablonConsent = resolveSellerMarketingTemplate({
    consentText: config.consent?.sellerMarketingText,
    texts: config.texts,
    locale,
    defaultLocale: config.identity.defaultLocale,
  });
  const sellerConsentLabel =
    seller === null
      ? ""
      : esteGrup
        ? groupMarketingText(sablonConsent, { displayName: seller.displayName, taxId: seller.taxId }, destinatari)
        : sellerMarketingText(sablonConsent, seller.displayName);

  const formValid =
    customer.name.trim().length >= 3 &&
    isAcceptablePhone(customer.phone) &&
    isPlausibleEmail(customer.email) &&
    customer.termsAccepted &&
    (!customer.billingRequested || customer.billingTaxId.trim().length >= 2);

  const submit = async (): Promise<void> => {
    setShowValidation(true);
    setSubmitError(null);
    const purchaseEventId = consentAllows(consentState, "marketing") ? newEventId() : null;
    const metaCookies = readMetaCookies();
    if (seller === null || paymentMethod === null || blockers.length > 0 || !formValid) {
      // Primul camp gresit intra in vedere: pe telefon eroarea poate fi cu doua
      // ecrane mai sus de butonul apasat.
      const firstInvalid = document.querySelector<HTMLElement>("[aria-invalid='true']");
      firstInvalid?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const extraNotes = [customer.notes.trim(), customer.changeFor.length > 0 ? `Rest din ${customer.changeFor} lei` : ""]
      .filter((part) => part.length > 0)
      .join(". ");

    const payload: CheckoutRequest = {
      locale,
      quoteId: currentQuote.quoteId,
      acceptedFoodFactsFingerprint: currentQuote.foodFactsFingerprint,
      acceptedSellerVendorId: seller.vendorId,
      customer: {
        name: customer.name.trim(),
        phone: customer.phone.trim(),
        email: customer.email.trim().length > 0 ? customer.email.trim() : null,
      },
      address,
      paymentMethod,
      billing: customer.billingRequested
        ? { taxId: customer.billingTaxId.trim(), legalName: customer.billingLegalName.trim(), address: customer.billingAddress.trim() }
        : null,
      notes: extraNotes.length > 0 ? extraNotes : null,
      marketingOptIn: customer.marketingOptIn,
      sellerMarketingConsent: {
        vendorId: seller.vendorId,
        granted: bifaMarketingDisponibila && customer.marketingOptIn,
        sellerName: seller.displayName,
        textVersion: consentTextVersion,
        textSnapshot: sellerConsentLabel,
      },
      trackingEventId: purchaseEventId,
      trackingFbp: purchaseEventId === null ? null : metaCookies.fbp,
      trackingFbc: purchaseEventId === null ? null : metaCookies.fbc,
    };

    setSubmitting(true);
    try {
      const response = await submitCheckout(marketKey, payload);
      if (purchaseEventId !== null) {
        trackEvent(
          "Purchase",
          { value: currentQuote.totals.total / 100, currency: currentQuote.totals.currency, order_id: response.platformOrderCode },
          purchaseEventId,
        );
      }
      // Urmarirea cere cod + telefon; perechea se retine aici, o singura data,
      // impreuna cu ce s-a comandat — pentru „Comanda din nou" si lista de comenzi.
      rememberOrder(marketKey, response.platformOrderCode, payload.customer.phone, {
        placedAt: new Date().toISOString(),
        storefrontSlug,
        storefrontName: session.storefrontName,
        sellerName: seller.displayName,
        total: response.totals.total,
        lines: rememberedLinesFromCart(lines),
      });
      if (customer.rememberMe) {
        writeCustomerProfile(marketKey, { name: payload.customer.name, phone: payload.customer.phone, email: payload.customer.email ?? "" });
      } else {
        clearCustomerProfile(marketKey);
      }
      // Se golesc doar liniile. Adresa ramane: e ce va folosi clientul si la
      // urmatoarea comanda.
      clearLines();
      // Voucherul s-a consumat pe comanda asta: lasat in sesiune, urmatorul cos
      // ar primi VOUCHER_INVALID la prima cotatie si ar bloca continuarea.
      setVoucherCode(null);
      navigate(`/comanda/${encodeURIComponent(response.platformOrderCode)}`, { replace: true });
    } catch (error: unknown) {
      const apiError =
        error instanceof ApiError
          ? error
          : new ApiError({ status: 0, code: "UNEXPECTED", message: error instanceof Error ? error.message : "Comanda nu a putut fi trimisă." });
      setSubmitError(apiError);
      if (apiError.code === CORE_ERROR_CODES.QUOTE_EXPIRED || apiError.code === CORE_ERROR_CODES.SELLER_MISMATCH) {
        quote.reload();
      }
      // Voucherul a fost refuzat abia la confirmare (alt telefon, consumat sau
      // anulat intre timp): retrimiterea ar da acelasi 409 la nesfarsit. Se scoate
      // din sesiune si comanda se recalculeaza fara el — clientul vede totalul nou.
      if (apiError.code === CORE_ERROR_CODES.VOUCHER_INVALID) {
        setVoucherCode(null);
        quote.reload();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const etaFrom = formatClock(currentQuote.eta.deliveryFrom, config.hours.timezone);
  const etaTo = formatClock(currentQuote.eta.deliveryTo, config.hours.timezone);
  const sellerPhone = seller !== null && isUsableSellerPhone(seller.phone) ? seller.phone : isUsableSellerPhone(config.identity.supportPhone) ? config.identity.supportPhone : null;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 pb-36">
      <div className="flex items-center justify-between gap-3">
        <SectionHeading level={1}>Finalizare</SectionHeading>
        <Link to="/cos" className="text-sm font-semibold text-brand-on-surface hover:underline">
          Modifică coșul
        </Link>
      </div>

      {sellerChanged && seller !== null ? (
        <Alert tone="warning" title="Vânzătorul s-a schimbat" actions={<Button onClick={() => acceptSeller(seller.vendorId)}>{`Continuă cu ${seller.legalName}`}</Button>}>
          Comanda ta va fi pregătită și facturată de altă firmă decât cea afișată anterior. Citește datele de mai jos și confirmă schimbarea înainte de a trimite comanda.
        </Alert>
      ) : null}

      {coverageIncomplete ? (
        <Alert tone="error" title="Coșul nu poate fi acoperit integral" actions={<Button variant="secondary" onClick={() => navigate("/cos")}>Înapoi la coș</Button>}>
          <p>Restaurantul afișat nu poate pregăti toate produsele din coș. Scoate produsele de mai jos sau alege altă vitrină.</p>
          <ul className="mt-2 list-disc pl-5">
            {missingLines.length > 0 ? missingLines.map((line) => <li key={line.lineId}>{line.name}</li>) : currentQuote.coverage.missingOfferIds.map((id) => <li key={id}>{id}</li>)}
          </ul>
        </Alert>
      ) : null}

      {/* 1. Livrarea: unde si cand. */}
      <Section id="livrare-titlu" title="Livrare">
        <div className="da-card divide-y divide-line">
          <button type="button" onClick={() => addressSheet.open()} className="da-tap flex w-full items-start gap-3 p-4 text-left hover:bg-surface-hover">
            <IconPin size={22} className="mt-0.5 shrink-0 text-brand-on-surface" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold text-ink">{formatAddress(address)}</span>
              <span className="block text-xs text-ink-muted">
                {[formatAddressDetails(address), address.notes ?? ""].filter((part) => part.length > 0).join(" · ") || "Fără detalii pentru curier — adaugă bloc, etaj, interfon"}
              </span>
            </span>
            <span className="inline-flex shrink-0 items-center gap-1 text-sm font-bold text-brand-on-surface">
              <IconEdit size={16} /> Modifică
            </span>
          </button>
          <div className="flex items-start gap-3 p-4">
            <IconClock size={22} className="mt-0.5 shrink-0 text-brand-on-surface" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-ink">{`Ajunge azi între ${etaFrom} și ${etaTo}`}</p>
              <p className="text-xs text-ink-muted">
                {currentQuote.eta.confidence === "cached"
                  ? "Interval estimat din livrările anterioare, nu din starea de acum a bucătăriei."
                  : currentQuote.eta.confidence === "low"
                    ? "Interval orientativ: bucătăria are un volum neobișnuit acum."
                    : currentQuote.degraded === true
                      ? "Estimarea nu a putut fi actualizată din bucătărie chiar acum."
                      : "Cât mai repede posibil. Curierul te sună dacă întârzie."}
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* 2. Contactul. */}
      <Section id="date-contact" title="Datele tale">
        <div className="da-card flex flex-col gap-4 p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Nume și prenume"
              required
              autoComplete="name"
              autoCorrect="off"
              value={customer.name}
              error={nameError}
              onBlur={() => setTouched((current) => ({ ...current, name: true }))}
              onChange={(event) => setCustomer((current) => ({ ...current, name: event.target.value }))}
            />
            <Field
              label="Telefon"
              required
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="07xx xxx xxx"
              value={customer.phone}
              error={phoneError}
              hint="Curierul te sună la acest număr. Pe el urmărești și comanda."
              onBlur={() => setTouched((current) => ({ ...current, phone: true }))}
              onChange={(event) => setCustomer((current) => ({ ...current, phone: event.target.value }))}
            />
          </div>
          <Field
            label="E-mail"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            hint="Primești confirmarea comenzii, cu datele vânzătorului."
            value={customer.email}
            error={emailError}
            onBlur={() => setTouched((current) => ({ ...current, email: true }))}
            onChange={(event) => setCustomer((current) => ({ ...current, email: event.target.value }))}
          />
          <label className="flex items-start gap-3 text-sm text-ink">
            <input type="checkbox" className="mt-1" checked={customer.rememberMe} onChange={(event) => setCustomer((current) => ({ ...current, rememberMe: event.target.checked }))} />
            <span>
              Reține datele pe acest dispozitiv
              <span className="block text-xs text-ink-muted">Data viitoare nu le mai scrii. Nu se creează niciun cont.</span>
            </span>
          </label>
        </div>
      </Section>

      {/* 3. Plata. */}
      <Section id="plata-titlu" title="Plata">
        <fieldset className="da-card flex flex-col gap-2 p-4">
          <legend className="sr-only">Metoda de plată</legend>
          {allowedMethods.length === 0 ? (
            <p role="alert" className="text-sm text-danger">Nicio metodă de plată nu poate fi oferită pentru această comandă.</p>
          ) : (
            allowedMethods.map((method) => (
              <ChoiceRow
                key={method}
                type="radio"
                name="metoda-plata"
                value={method}
                checked={paymentMethod === method}
                onChange={() => setPaymentMethod(method)}
                title={PAYMENT_LABELS[method]?.label ?? method}
                description={PAYMENT_LABELS[method]?.hint}
                trailing={<IconCash size={22} className="shrink-0 text-ink-muted" />}
              />
            ))
          )}
          {unnamedMethodCount === 0 ? null : (
            <p className="text-sm text-warning">Serverul acceptă și o metodă de plată pe care această versiune a aplicației nu o cunoaște. Reîncarcă pagina ca să o poți folosi.</p>
          )}
          {config.payments.changeForBanknotes === undefined || config.payments.changeForBanknotes.length === 0 || paymentMethod !== "cash_on_delivery" ? null : (
            <label className="mt-1 flex flex-col gap-1.5 text-sm">
              <span className="font-semibold text-ink">Ai nevoie de rest?</span>
              <select
                className="w-full rounded-control border border-line bg-canvas px-3 py-3 text-base text-ink"
                value={customer.changeFor}
                onChange={(event) => setCustomer((current) => ({ ...current, changeFor: event.target.value }))}
              >
                <option value="">Am suma exactă</option>
                {config.payments.changeForBanknotes.map((banknote) => (
                  <option key={banknote} value={String(banknote)}>{`Plătesc cu ${banknote} lei`}</option>
                ))}
              </select>
            </label>
          )}
        </fieldset>
      </Section>

      {/* 4. Comanda. */}
      <Section
        id="comanda-titlu"
        title="Comanda ta"
        action={
          <Link to="/cos" className="text-sm font-semibold text-brand-on-surface hover:underline">
            Modifică
          </Link>
        }
      >
        <div className="da-card divide-y divide-line">
          <ul className="divide-y divide-line">
            {lines.map((line) => {
              const priced = quotedPriceForLine(line, currentQuote);
              const unitPriceGross = priced?.customerUnitPrice ?? priced?.unitPriceGross ?? line.unitPriceGross;
              const compareAtUnitPrice =
                typeof priced?.compareAtUnitPrice === "number" && priced.compareAtUnitPrice > unitPriceGross ? priced.compareAtUnitPrice : null;
              return (
                <li key={line.lineId} className="flex items-start justify-between gap-3 px-4 py-3 text-sm">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink">
                      <span className="mr-2 inline-block min-w-6 rounded bg-surface-raised px-1 text-center text-xs font-bold tabular-nums">{line.qty}×</span>
                      {line.name}
                    </p>
                    {line.optionLabels.length === 0 ? null : <p className="text-xs text-ink-muted">{line.optionLabels.join(" · ")}</p>}
                    {line.notes === null ? null : <p className="text-xs italic text-ink-muted">„{line.notes}"</p>}
                  </div>
                  <div className="shrink-0 text-right">
                    <Price amount={unitPriceGross * line.qty} className="block font-semibold text-ink" />
                    {compareAtUnitPrice === null ? null : <Price amount={compareAtUnitPrice * line.qty} className="block text-xs text-ink-muted line-through" />}
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="flex flex-col gap-3 p-4">
            {notesOpen || customer.notes.length > 0 ? (
              <TextArea
                label="Mențiuni pentru comandă"
                quiet
                rows={2}
                maxLength={400}
                hint="Pentru restaurant: cum să pregătească; pentru curier: detaliile sunt la adresă."
                value={customer.notes}
                onChange={(event) => setCustomer((current) => ({ ...current, notes: event.target.value }))}
              />
            ) : (
              <button type="button" onClick={() => setNotesOpen(true)} className="self-start text-sm font-semibold text-brand-on-surface underline">
                + Adaugă o mențiune pentru comandă
              </button>
            )}
            <label className="flex items-start gap-3 text-sm text-ink">
              <input type="checkbox" className="mt-1" checked={customer.billingRequested} onChange={(event) => setCustomer((current) => ({ ...current, billingRequested: event.target.checked }))} />
              <span>
                Vreau factură pe firmă
                <span className="block text-xs text-ink-muted">Datele merg la restaurantul vânzător, care emite factura conform legii.</span>
              </span>
            </label>
            {customer.billingRequested ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label="CUI"
                  required
                  value={customer.billingTaxId}
                  error={billingError}
                  onBlur={() => setTouched((current) => ({ ...current, billingTaxId: true }))}
                  onChange={(event) => setCustomer((current) => ({ ...current, billingTaxId: event.target.value }))}
                />
                <Field label="Denumire firmă" value={customer.billingLegalName} onChange={(event) => setCustomer((current) => ({ ...current, billingLegalName: event.target.value }))} />
                <div className="sm:col-span-2">
                  <Field label="Sediu" value={customer.billingAddress} onChange={(event) => setCustomer((current) => ({ ...current, billingAddress: event.target.value }))} />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {!foodFactsReady ? (
          <Alert tone="error" title="Alergenii nu sunt confirmați" actions={<Button size="sm" variant="secondary" onClick={quote.reload}>Reîncarcă estimarea</Button>}>
            Reîncarcă estimarea înainte de a trimite comanda.
          </Alert>
        ) : (
          <details className="da-card group">
            <summary className="da-tap flex cursor-pointer items-center gap-2 px-4 py-3 text-sm font-semibold text-ink">
              <IconWarning size={18} className={allAllergens.length > 0 ? "text-warning" : "text-ink-muted"} />
              <span className="flex-1">
                {allAllergens.length > 0 ? `Alergeni în comandă: ${allAllergens.join(", ")}` : "Alergeni și valori nutriționale"}
              </span>
              <span className="text-xs font-normal text-ink-muted group-open:hidden">Detalii</span>
            </summary>
            <div className="flex flex-col gap-3 border-t border-line p-4">
              {currentQuote.foodFacts.map((facts, index) => {
                const line = lines.find((item) => item.offerId === facts.offerId);
                return (
                  <div key={`${facts.offerId}:${facts.selectedOptionIds.join(",")}:${index}`} className="text-sm">
                    <p className="font-semibold text-ink">{line?.name ?? "Produs"}</p>
                    <p className={facts.allergens.length > 0 ? "mt-0.5 text-warning" : "mt-0.5 text-ink-muted"}>
                      {facts.allergens.length > 0 ? `Conține/declară: ${facts.allergens.join(", ")}` : "Nu are alergeni declarați în configurația cotată."}
                    </p>
                    {facts.dietTags.length === 0 ? null : <p className="mt-0.5 text-xs text-ink-muted">Confirmat: {facts.dietTags.join(", ")}</p>}
                    {(facts.selectedOptionNutrition ?? []).map((entry) => {
                      const rows = nutritionRows(entry.nutrition);
                      if (rows.length === 0) return null;
                      const optionIndex = line?.selectedOptions.findIndex((option) => option.optionId === entry.optionId) ?? -1;
                      const label = optionIndex >= 0 ? line?.optionLabels[optionIndex] : null;
                      return (
                        <details key={entry.optionId} className="mt-2 rounded-control border border-line px-3 py-2">
                          <summary className="cursor-pointer text-xs font-semibold text-ink">
                            {label ?? "Extra selectat"} — valori nutriționale {nutritionBasisLabel(entry.nutrition)}
                          </summary>
                          <dl className="mt-2 text-xs">
                            {rows.map((row) => (
                              <div key={row.label} className="flex justify-between gap-3 border-t border-line py-1.5">
                                <dt className="text-ink-muted">{row.label}</dt>
                                <dd className="font-semibold text-ink">{row.value}</dd>
                              </div>
                            ))}
                          </dl>
                        </details>
                      );
                    })}
                  </div>
                );
              })}
              <p className="text-xs text-ink-muted">Dacă declarația se schimbă înainte de trimitere, comanda se oprește și îți cerem o confirmare nouă.</p>
            </div>
          </details>
        )}
      </Section>

      {/* 5. Totalul. */}
      <TotalsPanel totals={totals} sellerCount={seller === null ? 0 : 1} />

      {/* 6. Vanzatorul (ADR-003) si acordurile. */}
      {seller === null ? (
        <Alert tone="error" title="Vânzătorul nu este stabilit">Comanda nu poate fi confirmată până când nu se stabilește firma care o pregătește și o vinde.</Alert>
      ) : (
        <SellerIdentityCard sellers={[seller]} supportPhone={config.identity.supportPhone} />
      )}

      <div className="flex flex-col gap-3">
        <label className="flex items-start gap-3 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-1"
            checked={customer.termsAccepted}
            aria-invalid={termsError !== null || undefined}
            onChange={(event) => setCustomer((current) => ({ ...current, termsAccepted: event.target.checked }))}
          />
          <span>
            Am citit și accept{" "}
            <a className="underline" href="/legal" target="_blank" rel="noreferrer">termenii și condițiile</a> și{" "}
            <a className="underline" href="/legal/confidentialitate" target="_blank" rel="noreferrer">politica de confidențialitate</a>.
          </span>
        </label>
        {termsError === null ? null : <p role="alert" className="text-sm font-medium text-danger">{termsError}</p>}

        <label className={`flex items-start gap-3 text-sm text-ink ${seller === null || !bifaMarketingDisponibila ? "hidden" : ""}`}>
          <input type="checkbox" className="mt-1" checked={customer.marketingOptIn} onChange={(event) => setCustomer((current) => ({ ...current, marketingOptIn: event.target.checked }))} />
          <span className="flex flex-col gap-0.5">
            <span>{sellerConsentLabel}</span>
            <span className="text-xs text-ink-muted">{t("consent.seller_marketing_hint", CONSENT_TEXTS_RO.sellerMarketingHint)}</span>
          </span>
        </label>
      </div>

      {submitError === null ? null : (
        <Alert tone="error" title="Comanda nu a fost trimisă" actions={reloadRequired ? <Button onClick={() => window.location.reload()}>Reîncarcă aplicația</Button> : undefined}>
          <p>{submitError.message}</p>
          {submitError.code !== CORE_ERROR_CODES.VOUCHER_INVALID ? null : (
            <p className="mt-1">Voucherul a fost scos din comandă, iar totalul de mai sus e recalculat fără el. Verifică suma și trimite din nou.</p>
          )}
          {!reloadRequired ? null : <p className="mt-1">Aplicația și serverul nu mai vorbesc aceeași versiune. Reîncarcă pagina; coșul și adresa rămân salvate.</p>}
          {submitError.requestId === undefined ? null : <p className="mt-2 text-xs text-ink-muted">{`Referință pentru asistență: ${submitError.requestId}`}</p>}
        </Alert>
      )}

      {blockers.length === 0 ? null : (
        <Alert tone="warning" title="Comanda nu poate fi trimisă încă">
          <ul className="list-disc pl-5">
            {blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Bara lipita: firma cu care se contracteaza (ADR-003), totalul si butonul. */}
      <div className="da-bottom-bar">
        <div className="mx-auto flex max-w-3xl flex-col gap-2 px-4 py-3">
          {seller === null ? null : (
            <p className="da-clamp-1 text-xs text-ink-muted">
              <span className="font-semibold text-ink">{`Vânzător: ${seller.legalName}`}</span>
              {` · CUI ${seller.taxId}${sellerPhone === null ? "" : ` · ${sellerPhone}`}`}
            </p>
          )}
          <div className="flex items-center gap-3">
            {/* Totalul si CUM se plateste stau in stanga, pe doua randuri scurte;
                butonul ramane scurt, ca sa nu impinga totalul pe patru randuri pe
                telefon. Suma apare o singura data — dublata in buton, nu incapea. */}
            <div className="min-w-0 flex-1 leading-tight">
              <span className="block whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-ink-muted">
                {paymentMethod === "cash_on_delivery" ? "De plată la ușă" : "Total de plată"}
              </span>
              <Price amount={totals.total} className="block whitespace-nowrap text-xl font-extrabold text-ink" />
            </div>
            <Button size="lg" loading={submitting} disabled={blockers.length > 0} onClick={() => void submit()} className="shrink-0 px-6">
              {paymentMethod === "cash_on_delivery" ? "Trimite comanda" : "Confirmă comanda"}
            </Button>
          </div>
          <p className="text-center text-[11px] text-ink-muted">
            {paymentMethod === "cash_on_delivery"
              ? `Plătești ${formatRon(totals.total)} numerar curierului. Prin confirmare închei contractul de vânzare cu firma de mai sus.`
              : "Prin confirmare închei contractul de vânzare cu firma de mai sus."}
          </p>
        </div>
      </div>

    </div>
  );
}
